<template>
  <div class="performance-chart" :class="{ 'dark-mode': isDarkMode }">
    <svg
      :width="width"
      :height="height"
      :viewBox="`0 0 ${width} ${height}`"
      preserveAspectRatio="xMidYMid meet"
    >
      <!-- Grid lines (horizontal) -->
      <line
        v-for="(line, index) in gridLines"
        :key="`grid-${index}`"
        :x1="paddingLeft"
        :y1="line.y"
        :x2="width - paddingRight"
        :y2="line.y"
        :stroke="gridColor"
        stroke-width="1"
        stroke-dasharray="4 4"
      />

      <!-- Y-axis labels -->
      <text
        v-for="(label, index) in yAxisLabels"
        :key="`ylabel-${index}`"
        :x="paddingLeft - 10"
        :y="label.y + 4"
        :fill="textColor"
        font-size="10"
        font-family="Avenir, sans-serif"
        text-anchor="end"
      >
        {{ label.text }}
      </text>

      <!-- Area fill (gradient) -->
      <defs>
        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" :stop-color="lineColor" stop-opacity="0.3" />
          <stop offset="100%" :stop-color="lineColor" stop-opacity="0.05" />
        </linearGradient>
      </defs>

      <path :d="areaPath" fill="url(#areaGradient)" />

      <!-- Main line -->
      <path
        :d="linePath"
        :stroke="lineColor"
        stroke-width="2.5"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <!-- Data points -->
      <circle
        v-for="(point, index) in points"
        :key="`point-${index}`"
        :cx="point.x"
        :cy="point.y"
        :r="pointRadius"
        :fill="lineColor"
        :stroke="isDarkMode ? '#2d3540' : '#ffffff'"
        stroke-width="2"
      />

      <!-- X-axis labels -->
      <text
        v-for="(label, index) in xAxisLabels"
        :key="`label-${index}`"
        :x="label.x"
        :y="height - paddingBottom + 20"
        :fill="textColor"
        font-size="11"
        font-family="Avenir, sans-serif"
        text-anchor="middle"
      >
        {{ label.text }}
      </text>
    </svg>
  </div>
</template>

<script>
export default {
  name: "PerformanceChart",
  props: {
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: 600,
    },
    height: {
      type: Number,
      default: 200,
    },
  },
  data() {
    return {
      paddingTop: 20,
      paddingRight: 20,
      paddingBottom: 30,
      paddingLeft: 50,
      pointRadius: 4,
      // Sample data representing performance over time (values in thousands)
      data: [
        { label: "1/02", value: 45000 },
        { label: "3/02", value: 52000 },
        { label: "6/02", value: 48000 },
        { label: "9/02", value: 65000 },
        { label: "12/02", value: 70000 },
        { label: "15/02", value: 68000 },
        { label: "18/02", value: 75000 },
        { label: "21/02", value: 82000 },
        { label: "24/02", value: 78000 },
        { label: "27/02", value: 85000 },
        { label: "30/02", value: 88000 },
      ],
    };
  },
  computed: {
    lineColor() {
      return this.isDarkMode ? "#4CAF50" : "#2F80ED";
    },
    gridColor() {
      return this.isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
    },
    textColor() {
      return this.isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
    },
    chartWidth() {
      return this.width - this.paddingLeft - this.paddingRight;
    },
    chartHeight() {
      return this.height - this.paddingTop - this.paddingBottom;
    },
    maxValue() {
      return 120000; // Fixed max at 120K
    },
    minValue() {
      return 0; // Fixed min at 0
    },
    points() {
      const stepX = this.chartWidth / (this.data.length - 1);
      const valueRange = this.maxValue - this.minValue || 1;

      return this.data.map((item, index) => {
        const x = this.paddingLeft + index * stepX;
        const normalizedValue = (item.value - this.minValue) / valueRange;
        const y = this.paddingTop + this.chartHeight - normalizedValue * this.chartHeight;

        return { x, y, value: item.value };
      });
    },
    linePath() {
      if (this.points.length === 0) return "";

      let path = `M ${this.points[0].x} ${this.points[0].y}`;

      // Smooth curve using quadratic bezier curves
      for (let i = 1; i < this.points.length; i++) {
        const prevPoint = this.points[i - 1];
        const currentPoint = this.points[i];

        const midX = (prevPoint.x + currentPoint.x) / 2;

        path += ` Q ${prevPoint.x} ${prevPoint.y}, ${midX} ${(prevPoint.y + currentPoint.y) / 2}`;
        path += ` Q ${currentPoint.x} ${currentPoint.y}, ${currentPoint.x} ${currentPoint.y}`;
      }

      return path;
    },
    areaPath() {
      if (this.points.length === 0) return "";

      const bottomY = this.paddingTop + this.chartHeight;

      let path = this.linePath;

      // Close the path at the bottom
      path += ` L ${this.points[this.points.length - 1].x} ${bottomY}`;
      path += ` L ${this.points[0].x} ${bottomY}`;
      path += " Z";

      return path;
    },
    gridLines() {
      const lines = [];
      const numLines = 5; // 0, 30K, 60K, 90K, 120K
      const step = this.chartHeight / (numLines - 1);

      for (let i = 0; i < numLines; i++) {
        lines.push({
          y: this.paddingTop + i * step,
          value: this.maxValue - (i * this.maxValue / (numLines - 1))
        });
      }

      return lines;
    },
    yAxisLabels() {
      // Generate labels: 120K, 90K, 60K, 30K, 0
      const labels = [];
      const numLabels = 5;
      const step = this.chartHeight / (numLabels - 1);

      for (let i = 0; i < numLabels; i++) {
        const value = this.maxValue - (i * this.maxValue / (numLabels - 1));
        const y = this.paddingTop + i * step;

        // Format value: 120000 -> "120K"
        const formattedValue = value >= 1000 ? `${Math.round(value / 1000)}K` : value.toString();

        labels.push({
          y: y,
          text: formattedValue,
        });
      }

      return labels;
    },
    xAxisLabels() {
      // Show every other label to avoid crowding
      return this.data
        .filter((_, index) => index % 2 === 0)
        .map((item, index) => {
          const dataIndex = index * 2;
          return {
            x: this.points[dataIndex].x,
            text: item.label,
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.performance-chart {
  width: 100%;
  height: auto;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px 12px;
  box-sizing: border-box;

  &.dark-mode {
    background: #2d3540;
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
}
</style>
